import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { differenceInDays, subDays, format, addDays } from 'date-fns';

// ⛔ LEI DOS 3 DONOS (Douglas 06/09/2026) — posse de lead se grava pela ROTA.
//
// Ate 06/09 estes hooks escreviam `leads_carteira.consultor_id` e ate'
// `donos_cliente.owner_consultor_id` direto pelo client, pulando as travas do
// backend. O pior nao era a falta de cadeado: era a MENTIRA. O gatilho
// fn_realinhar_lead_aberto_ao_dono_update devolve o lead ao dono do CNPJ em
// silencio — o PostgREST responde 200, a tela diz "transferido" e ainda
// notifica "Voce recebeu N leads!" sem uma linha ter mudado. Aconteceu 22 vezes
// com 18 leads, um deles tentado 3 dias seguidos.
const API_BASE = () =>
  (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

/**
 * POST autenticado numa rota do backend. Devolve o `data` desembrulhado; erro da
 * rota vira Error com a FRASE que ela mandou — a tela mostra o motivo ("so o
 * master transfere carteira") em vez de "Erro 403".
 */
/** O que `postarNaRota` levanta: a frase da rota + o codigo/status para decidir. */
export type ErroDaRota = Error & { codigo?: string; status?: number };

export async function postarNaRota<T = any>(caminhoV1: string, body: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente');
  const res = await fetch(`${API_BASE()}${caminhoV1}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const erro = new Error(json?.error || `Erro ${res.status}`) as ErroDaRota;
    erro.codigo = json?.codigo;
    erro.status = res.status;
    throw erro;
  }
  return (json && typeof json === 'object' && 'data' in json ? json.data : json) as T;
}

export interface VereditoDaRota {
  /** true = NADA mudou agora E a rota ABRIU o pedido de aprovacao. */
  fila: boolean;
  /** quantos casos do lote a rota enfileirou de verdade. */
  naFila: number;
  /**
   * Casos que a rota RECUSOU sem abrir pedido nenhum (`bloqueados[]`).
   * ⛔ Nao somar com `naFila`: barrado nao e' enfileirado. Ver o aviso em
   * `lerVeredito` — a frase que a rota manda junto do bloqueio promete uma fila
   * que ela nao criou.
   */
  barrados: number;
  /** a frase da rota, escrita para o consultor ler. */
  motivo?: string;
}

/**
 * Le o veredito sem exigir um formato unico. As rotas da lei dos 3 donos
 * respondem de dois jeitos hoje:
 *
 *  • 202 `{ aguardando_aprovacao: true, error: '<motivo>', solicitacao_id }`
 *    — atribuir-para-mim, quando o CNPJ nao e' DONO LEAD;
 *  • 200 `{ count, pedidos, bloqueados: [{ cliente_id, nivel, motivo }] }`
 *    — bulk/transferir, que decide CNPJ a CNPJ.
 *
 * ⚠️ 202 nao e' erro: `res.ok` e' true e a promessa resolve. Sem ler
 * `aguardando_aprovacao` aqui, a tela cantaria sucesso em cima de um pedido de
 * aprovacao — que e' exatamente a mentira que esta frente veio consertar.
 * Rota que nao mandar nada disso cai em `fila: false` e o texto de sucesso e' o
 * de sempre; nunca o contrario, que seria inventar uma fila que nao existe.
 *
 * ⛔ `bloqueados[]` NAO E' FILA — e por isso ele conta em `barrados`, separado.
 * Quem escreve o `motivo` la' dentro e' `decidirTrocaDeDono`, e a frase dela
 * termina em "seu pedido foi para a fila de Aprovacoes, aba 'Com o master'".
 * Essa frase foi escrita para as portas que CHAMAM `abrirSolicitacaoDeTroca`
 * (atribuir-para-mim e assumir-pool). O `bulk/transferir` nao chama: ele so'
 * devolve o cliente em `bloqueados` e escreve um warn no log. Somar bloqueado
 * como fila fazia a tela prometer uma aprovacao que ninguem ia ver — a mesma
 * mentira de antes, com roupa nova.
 */
export function lerVeredito(data: any): VereditoDaRota {
  const bloqueados = Array.isArray(data?.bloqueados) ? data.bloqueados : [];
  const naFila = Number(data?.naFila ?? data?.enviadosParaFila ?? data?.pendentes ?? 0) || 0;
  const marcado =
    data?.aguardando_aprovacao === true ||
    data?.viaFila === true ||
    String(data?.via ?? '').toUpperCase() === 'FILA' ||
    String(data?.statusFila ?? data?.status ?? '').startsWith('pendente_') ||
    // Devolver o id da solicitacao ja' prova que virou pedido de aprovacao.
    !!(data?.solicitacao_id ?? data?.solicitacaoId);
  const motivo =
    (typeof data?.motivo === 'string' && data.motivo) ||
    (data?.aguardando_aprovacao === true && typeof data?.error === 'string' && data.error) ||
    undefined;
  return { fila: marcado, naFila, barrados: bloqueados.length, motivo };
}

export interface PnDesfechoLead {
  lead_id: string;
  numero_pedido: string;
  concluidas: number;
  total: number;
  completo: boolean;
  concluido_em?: string | null;
  origem: 'lead' | 'cnpj_historico';
}

export interface LeadCarteira {
  id: string;
  lead_numero?: number; // Número sequencial do lead (#L1, #L2...)
  // ⚠️ Lead PF (lista da Oi, 09/09/2026) nao tem CNPJ: o documento dele e' o
  // cpf. Um lead nunca tem os dois — o banco recusa. Quem for mostrar documento
  // na tela decide por tipo_pessoa, nao por "cnpj vazio".
  cnpj: string | null;
  tipo_pessoa?: 'PJ' | 'PF';
  cpf?: string | null;
  razao_social: string;
  nome_fantasia?: string;
  uf: string;
  territorio?: string;
  carteira?: string;
  tempo_contrato_voz?: string;
  tempo_contrato_banda_larga?: string;
  qtd_linhas_voz: number;
  qtd_linhas_banda_larga: number;
  classificacao?: string;
  ddd?: string;
  telefone?: string;
  telefone_2?: string;
  telefone_3?: string;
  telefone_4?: string;
  telefone_adm?: string;
  nome_adm?: string;
  status_aptidao: string;
  status_lead: string;
  e_cliente_carteira: boolean;
  consultor_id?: string;
  atribuido_por?: string;
  data_atribuicao?: string;
  data_limite_contato?: string;
  data_limite_pedido?: string;
  data_limite_mkt?: string;
  contato_real_encontrado: boolean;
  contato_real_nome?: string;
  contato_real_telefone?: string;
  contato_real_cargo?: string;
  contato_real_email?: string;
  data_contato_encontrado?: string;
  cliente_id?: string;
  pedido_id?: string;
  data_conversao?: string;
  importacao_id: string;
  mes_referencia: string;
  created_at: string;
  updated_at: string;
  pn_desfecho?: PnDesfechoLead | null;
  // NOVOS CAMPOS EDITÁVEIS
  email?: string;
  tel_sigma1?: string;
  tel_sigma2?: string;
  linhas_fatura?: number;
  status_prospeccao?: string;
  plano_atual?: string;
  tipo_produto?: string;
  cobertura_fibra?: 'SIM' | 'NÃO';
  endereco_empresa?: string;
  // Os DOIS endereços do lead Algar: `endereco_empresa` é o cadastral da Receita
  // (o que define uf/cidade e o território) e estes são o endereço da VENDA —
  // onde o cliente PEDIU o serviço. Podem ser o mesmo lugar ou não.
  venda_logradouro?: string | null;
  venda_numero?: string | null;
  venda_complemento?: string | null;
  venda_bairro?: string | null;
  venda_cep?: string | null;
  venda_cidade?: string | null;
  venda_uf?: string | null;
  venda_relacao?: 'MESMO' | 'MESMA_RUA' | 'MESMA_CIDADE' | 'OUTRA_CIDADE' | 'OUTRO_ESTADO' | 'INDEFINIDO' | null;
  venda_residencial?: boolean | null;
  venda_endereco_bruto?: string | null;
  data_ultima_interacao?: string;
  // CAMPOS CALCULADOS DA VIEW v_leads_carteira_supervisor
  consultor_nome?: string;
  equipe_id?: string;
  equipe_nome?: string;
  dias_restantes_prazo?: number;
  dias_com_consultor?: number;
  urgencia_devolucao?: 'VENCIDO' | 'URGENTE' | 'ATENCAO' | 'OK';
  // NOVOS CAMPOS FASE 1 - Sistema de Leads Completo
  // A lista tem que bater com o enum tipo_lead_enum do banco. Faltavam ALGAR e
  // ECOSSISTEMA_API, e o TypeScript passou a acusar as duas comparacoes do
  // ModalDetalhesLeadCompleto como "impossiveis" — sendo que rodam em producao.
  tipo_lead?: 'RENOVACAO_CLARO' | 'PORTABILIDADE_VIVO' | 'TT_CPF_CNPJ' | 'TAB'
    | 'MANUAL' | 'IA_FIXA' | 'WHATSAPP' | 'ECOSSISTEMA_API' | 'ALGAR'
    | 'DIARIOS_RF' | 'OI' | 'URA' | 'PORTABILIDADE_CPF' | 'RENOVACAO_CPF' | 'RETENCAO_CPF' | 'OI_CPF' | 'ALGAR_CPF' | 'RF_CPF';
  tabulacao_id?: string;
  dados_discadora?: any;
  operadora_atual?: string;
  conta_operadora?: string;
  doc_cpf?: string;
  nome_proprietario?: string;
  valor_fatura_atual?: number;
  modalidade_cobranca?: string;
  data_recebimento?: string;
  observacoes_consultor?: string;
  /** M IMPORTADO: como veio no mailing. Congelado de proposito. */
  tempo_contrato_voz_num?: number;
  /** M ATUAL: meses de contrato HOJE. E' este que o filtro "Meses" usa e o que
   *  a coluna M mostra — regravado dia 1 de cada mes e apos cada importacao de
   *  parque (fn_atualizar_m_atual_leads). */
  m_atual?: number | null;
  tempo_contrato_bl?: number;
  qtd_linhas_voz_num?: number;
  qtd_linhas_bl?: number;
  segmento?: string;
  produto?: string;
  data_limite_renovacao?: string;
  fatura_solicitada?: boolean;
  fatura_analisada?: boolean;
  proposta_enviada?: boolean;
  valor_proposta?: number;
  linhas_cpf?: number;
  tipo_linha?: string;
  ano_base?: number;
  // CAMPOS DA PROPOSTA COMPLETA
  proposta_gb_atual?: number;
  proposta_linhas_novo?: number;
  proposta_gb_novo?: number;
  proposta_diferencial?: string;
  proposta_mensagem_whats?: string;
  proposta_mensagem_retorno?: string;
  proposta_mensagem_fechamento?: string;
  // CAMPO LINHA CONTA FATURA (do cliente)
  linha_conta_fatura?: string;
  // URL CONVERSA BEE360 (da tabulação)
  url_conversa?: string;
  // CAMPOS DE FATURA DO MÊS (da view v_leads_carteira_supervisor)
  tem_fatura_mes_atual?: boolean;
  fatura_baixada_em?: string;
  status_solicitacao_mes?: string;
  solicitacao_criada_em?: string;
  linhas_fatura_detectadas?: number;
  status_contato_visual?: string;
  // 🆕 Dados do Master Lead (snapshot 360° para PORTABILIDADE_VIVO)
  dados_master_lead?: Record<string, unknown>;
  master_lead_id?: string;
  campanha_id?: string;
  campanha_nome?: string;
  sub_tipo_portabilidade?: string;
  origem_lead?: string;
  em_modo_foco?: boolean;
  origem_prime?: string;
  // NOVOS CAMPOS - Linha Fixa
  qtd_linhas_fixas?: number;
  m_fixa?: number;
  valor_fatura_fixa?: number;
  // Faturamento fixa (calculado via staging_parque_fixo — retornado pelo RPC)
  fixa_faturamento?: number;
  // CAMPOS TELEMETRIA (importação)
  qtd_linhas_telemetria?: number;
  tempo_contrato_telemetria?: number;
  // MSG EMAIL CLARO
  msg_email_claro?: string;
  // SITUAÇÃO NA CARTEIRA CLARO (atualizado a cada importação)
  situacao_carteira?: 'NOVA' | 'AUMENTOU' | 'REDUZIU' | 'SAIU' | 'ZEROU_VOZ' | 'SEM_ALTERACAO' | null;
  // DONO COMERCIAL
  dono_comercial_id?: string;
  dono_comercial_nome?: string;
  // Estado da carteira: 'ATIVO' (item de pedido ativado) | 'CARTEIRA' (carteirizado/tem pedido, não ativou) | 'LIVRE' (sem carteira)
  dono_carteira_status?: string | null;
}

interface UseLeadsCarteiraParams {
  consultorId?: string;
  statusLead?: string;
  statusAptidao?: string;
  eClienteCarteira?: boolean;
}

export function useLeadsCarteira(params?: UseLeadsCarteiraParams) {
  return useQuery({
    queryKey: ['leads-carteira', 'PAGINATED_2025', params],
    staleTime: 60000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
    retryDelay: 1000,
    queryFn: async () => {
      console.log('🔍 [useLeadsCarteira] Iniciando busca paginada da VIEW...');
      console.log('🔍 [useLeadsCarteira] Params:', params);
      
      let allLeads: LeadCarteira[] = [];
      let from = 0;
      const pageSize = 1000; // Limite máximo do PostgREST
      let hasMore = true;
      let totalCount = 0;

      while (hasMore) {
        const to = from + pageSize - 1;
        
        let query = supabase
          .from('v_leads_carteira_supervisor')
          .select('*', { count: 'exact' })
          .neq('status_aptidao', 'INATIVO')
          .neq('status_lead', 'DESCARTADO');

        // Aplicar filtros opcionais
        if (params?.consultorId) {
          query = query.eq('consultor_id', params.consultorId);
        }
        if (params?.statusLead) {
          query = query.eq('status_lead', params.statusLead);
        }
        if (params?.statusAptidao) {
          query = query.eq('status_aptidao', params.statusAptidao);
        }
        if (params?.eClienteCarteira !== undefined) {
          query = query.eq('e_cliente_carteira', params.eClienteCarteira);
        }

        const { data, error, count } = await query
          .order('urgencia_devolucao', { ascending: false, nullsFirst: false })
          .order('dias_restantes_prazo', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })
          .range(from, to);

        if (error) {
          console.error('❌ [useLeadsCarteira] ERRO:', error);
          throw error;
        }

        if (count !== null) {
          totalCount = count;
        }

        console.log(`✅ [useLeadsCarteira] Página ${from}-${to}: ${data?.length || 0} leads (Total no banco: ${totalCount})`);
        
        if (data && data.length > 0) {
          allLeads = [...allLeads, ...(data as LeadCarteira[])];
          from += pageSize;
          
          // Verificar se há mais dados
          if (data.length < pageSize || (totalCount > 0 && allLeads.length >= totalCount)) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      console.log(`🎯 [useLeadsCarteira] TOTAL CARREGADO: ${allLeads.length} de ${totalCount} leads`);
      return allLeads as LeadCarteira[];
    },
  });
}

export function useMeusLeadsCarteira(consultorId?: string) {
  return useQuery({
    queryKey: ['meus-leads-carteira', consultorId],
    queryFn: async () => {
      if (!consultorId) return { meusClientes: [], novosLeads: [], convertidos: [], leadsTabulacao: [] };

      // ✅ OTIMIZAÇÃO: Executar queries em PARALELO (3x mais rápido)
      const [meusClientesResult, novosLeadsResult, convertidosResult, leadsTabulacaoResult] = await Promise.all([
        // Meus Clientes (prioridade) - usando view para ter campos de fatura
        supabase
          .from('v_leads_carteira_supervisor')
          .select('*')
          .eq('consultor_id', consultorId)
          .eq('e_cliente_carteira', true)
          .neq('status_aptidao', 'INATIVO')
          .not('status_lead', 'in', '(CONVERTIDO,DESCARTADO,ATUALIZADO)')
          .order('data_limite_pedido', { ascending: true })
          .order('qtd_linhas_voz', { ascending: false })
          .range(0, 499), // Reduzido para 500 registros iniciais

        // Novos Leads - usando view para ter campos de fatura
        supabase
          .from('v_leads_carteira_supervisor')
          .select('*')
          .eq('consultor_id', consultorId)
          .eq('e_cliente_carteira', false)
          .in('status_lead', ['ATRIBUIDO', 'NOVO'])
          .neq('status_aptidao', 'INATIVO')
          .order('data_limite_contato', { ascending: true })
          .order('qtd_linhas_voz', { ascending: false })
          .range(0, 499), // Reduzido para 500 registros iniciais

        // Convertidos - usando view para ter campos de fatura
        supabase
          .from('v_leads_carteira_supervisor')
          .select('*')
          .eq('consultor_id', consultorId)
          .eq('status_lead', 'CONVERTIDO')
          .order('data_conversao', { ascending: false })
          .limit(50),

        // 🆕 Leads de Tabulação (tipo TAB) - novos vindos da discadora
        supabase
          .from('v_leads_carteira_supervisor')
          .select('*')
          .eq('consultor_id', consultorId)
          .eq('tipo_lead', 'TAB')
          .not('status_lead', 'in', '(CONVERTIDO,DESCARTADO)')
          .order('created_at', { ascending: false })
          .range(0, 199),
      ]);

      return {
        meusClientes: (meusClientesResult.data || []) as LeadCarteira[],
        novosLeads: (novosLeadsResult.data || []) as LeadCarteira[],
        convertidos: (convertidosResult.data || []) as LeadCarteira[],
        leadsTabulacao: (leadsTabulacaoResult.data || []) as LeadCarteira[],
      };
    },
    enabled: !!consultorId,
    staleTime: 60000, // ✅ Aumentado para 1 minuto
    placeholderData: (previousData) => previousData, // ✅ Mantém dados anteriores durante refetch
  });
}

export function useAtribuirLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      consultorId,
      atribuidoPor,
    }: {
      leadId: string;
      consultorId: string;
      atribuidoPor: string;
    }) => {
      // Escrita pela ROTA. NAO uso bulk/atribuir de proposito: ela grava
      // status NEGOCIANDO para destino vendedor, e o gatilho
      // fn_lead_status_reais_guard levanta excecao sem contato real comprovado —
      // o lote inteiro voltaria 500. Atribuir nao e' trabalhar (LEI 1c).
      return postarNaRota<{ count?: number }>('/v1/leads-carteira/bulk/update', {
        ids: [leadId],
        updates: {
          consultor_id: consultorId,
          atribuido_por: atribuidoPor,
          data_atribuicao: new Date().toISOString(),
          data_limite_contato: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          status_lead: 'DISPONIVEL',
        },
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
      const veredito = lerVeredito(data);
      if (veredito.fila) {
        toast.warning(veredito.motivo || 'Foi para aprovação — o lead ainda não mudou de consultor.');
        return;
      }
      // `count` e' o updateMany do banco: 0 = a linha nao existe mais (lead
      // apagado ou id velho da lista em cache). Cantar sucesso em cima de 0 e'
      // o mesmo erro que esta frente veio consertar, so' que sem gatilho.
      if (Number(data?.count ?? 1) === 0) {
        toast.warning('Nada mudou — o lead não foi encontrado. Atualize a lista e tente de novo.');
        return;
      }
      toast.success('Lead atribuído com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao atribuir lead: ' + error.message);
    },
  });
}

export function useRegistrarContatoReal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      nome,
      telefone,
      cargo,
      email,
    }: {
      leadId: string;
      nome: string;
      telefone: string;
      cargo?: string;
      email?: string;
    }) => {
      const { error } = await supabase
        .from('leads_carteira')
        .update({
          contato_real_encontrado: true,
          contato_real_nome: nome,
          contato_real_telefone: telefone,
          contato_real_cargo: cargo,
          contato_real_email: email,
        })
        .eq('id', leadId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira'] });
      toast.success('Contato registrado! Cliente carteirizado com sucesso.');
    },
    onError: (error) => {
      toast.error('Erro ao registrar contato: ' + error.message);
    },
  });
}

// ⛔ DEVOLVER LEAD SAIU (Douglas 22/09/2026). Aqui moravam useDevolverLead e
// useDevolverLeadsEmMassa. Devolver zerava o consultor do lead e o deixava em
// DISPONIVEL, sem tocar no dono do CNPJ — e quatro gatilhos reempurravam o lead
// de volta para o dono. Medido: dos 31.751 leads ja' devolvidos, 31.663 (99,7%)
// voltaram a ter consultor, e os 88 que ficaram sem estao TODOS em CNPJ com dono.
// As duas rotas do backend sairam junto. Para tirar o lead da frente: NEGADO,
// com motivo — igual ao descarte em massa, logo abaixo.

function calcularUrgencia(lead: any): string {
  if (lead.status_aptidao === 'APTO' && lead.data_limite_pedido) {
    const diasRestantes = Math.ceil(
      (new Date(lead.data_limite_pedido).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (diasRestantes < 0) return 'VENCIDO';
    if (diasRestantes <= 2) return 'URGENTE';
    if (diasRestantes <= 7) return 'ATENÇÃO';
  }
  if (lead.data_limite_contato) {
    const diasRestantes = Math.ceil(
      (new Date(lead.data_limite_contato).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (diasRestantes < 0) return 'VENCIDO';
    if (diasRestantes <= 1) return 'URGENTE';
    if (diasRestantes <= 3) return 'ATENÇÃO';
  }
  return 'OK';
}

function calcularDiasRestantes(dataLimite?: string): number | undefined {
  if (!dataLimite) return undefined;
  return Math.ceil(
    (new Date(dataLimite).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
}

export function useExportarLeadsCarteira() {
  return useMutation({
    mutationFn: async () => {
      console.log('📊 [EXPORTAR] Iniciando exportação de TODOS os leads...');
      
      let allLeads: any[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const to = from + pageSize - 1;
        
        const { data, error } = await supabase
          .from('v_leads_carteira_supervisor')
          .select('*')
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })
          .range(from, to);
        
        if (error) throw error;
        
        console.log(`✅ [EXPORTAR] Página ${from}-${to}: ${data?.length || 0} leads`);
        
        if (data && data.length > 0) {
          allLeads = [...allLeads, ...data];
          from += pageSize;
          
          if (data.length < pageSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      console.log(`🎯 [EXPORTAR] TOTAL EXPORTADO: ${allLeads.length} leads`);
      return allLeads;
    },
    onSuccess: async (leads) => {
      const { exportarLeadsParaExcel } = await import('@/lib/exportarLeadsExcel');
      
      // View v_leads_carteira_supervisor já inclui consultor_nome e campos calculados
      const leadsFormatados = leads.map(lead => ({
        ...lead,
        urgencia: lead.urgencia_devolucao || calcularUrgencia(lead),
        dias_restantes_contato: lead.dias_restantes_prazo || calcularDiasRestantes(lead.data_limite_contato),
        dias_restantes_pedido: calcularDiasRestantes(lead.data_limite_pedido),
      }));
      
      
      const dataHoje = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      exportarLeadsParaExcel(
        leadsFormatados,
        `Leads_Carteira_${dataHoje}`,
        'Todos os Leads'
      );
      
      toast.success(`${leads.length} leads exportados com sucesso!`);
    },
    onError: (error: any) => {
      toast.error('Erro ao exportar: ' + error.message);
    }
  });
}

export function useExportarMeusLeads() {
  return useMutation({
    mutationFn: async (consultorId: string) => {
      console.log('📊 [EXPORTAR MEUS] Exportando leads do consultor:', consultorId);
      
      // Função auxiliar para buscar com paginação
      const buscarComPaginacao = async (query: any) => {
        let allData: any[] = [];
        let from = 0;
        const pageSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const to = from + pageSize - 1;
          const { data } = await query.order('id', { ascending: true }).range(from, to);
          
          if (data && data.length > 0) {
            allData = [...allData, ...data];
            from += pageSize;
            if (data.length < pageSize) hasMore = false;
          } else {
            hasMore = false;
          }
        }
        
        return allData;
      };

      // Meus Clientes (com paginação)
      const meusClientes = await buscarComPaginacao(
        supabase
          .from('leads_carteira')
          .select('*')
          .eq('consultor_id', consultorId)
          .eq('e_cliente_carteira', true)
          .not('status_lead', 'in', '("CONVERTIDO","DESCARTADO")')
      );

      // Novos Leads (com paginação)
      const novosLeads = await buscarComPaginacao(
        supabase
          .from('leads_carteira')
          .select('*')
          .eq('consultor_id', consultorId)
          .eq('e_cliente_carteira', false)
          .eq('status_lead', 'ATRIBUIDO')
      );

      // Convertidos (limite 50 é OK)
      const { data: convertidos } = await supabase
        .from('leads_carteira')
        .select('*')
        .eq('consultor_id', consultorId)
        .eq('status_lead', 'CONVERTIDO')
        .order('data_conversao', { ascending: false })
        .limit(50);

      console.log(`✅ [EXPORTAR MEUS] Total: ${meusClientes.length} clientes, ${novosLeads.length} novos, ${convertidos?.length || 0} convertidos`);

      return {
        meusClientes,
        novosLeads,
        convertidos: convertidos || []
      };
    },
    onSuccess: async (data, consultorId) => {
      const { exportarLeadsConsultorExcel } = await import('@/lib/exportarLeadsExcel');
      
      const formatarLeads = (leads: any[]) => leads.map(lead => ({
        ...lead,
        urgencia: calcularUrgencia(lead),
        dias_restantes_contato: calcularDiasRestantes(lead.data_limite_contato),
        dias_restantes_pedido: calcularDiasRestantes(lead.data_limite_pedido),
      }));
      
      const { data: usuario } = await supabase
        .from('usuarios')
        .select('nome')
        .eq('id', consultorId)
        .single();
      
      exportarLeadsConsultorExcel(
        formatarLeads(data.meusClientes),
        formatarLeads(data.novosLeads),
        formatarLeads(data.convertidos),
        usuario?.nome || 'Consultor'
      );
      
      const total = data.meusClientes.length + data.novosLeads.length + data.convertidos.length;
      toast.success(`${total} leads exportados em 3 abas!`);
    },
    onError: (error: any) => {
      toast.error('Erro ao exportar: ' + error.message);
    }
  });
}

// ========== AÇÕES EM MASSA ==========

// Roles que são vendedores (leads vão para "Meus Leads" com NEGOCIANDO)
const ROLES_VENDEDORES = ['consultor', 'supervisor'];

// Quebra um array grande em lotes. Necessário porque o Supabase manda o filtro
// `.in('col', [...])` na URL — com centenas/milhares de UUIDs a URL estoura o
// limite do servidor e volta 400 Bad Request. 200 por lote fica bem abaixo do teto.
function emLotes<T>(arr: T[], tamanho = 200): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += tamanho) out.push(arr.slice(i, i + tamanho));
  return out;
}

// Atribuir/Distribuir leads em massa (suporta múltiplos usuários com round-robin e status dinâmico)
export function useAtribuirLeadsEmMassa() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadIds,
      consultorIds,
      atribuidoPor,
      leadsComClienteId,
    }: {
      leadIds: string[];
      consultorIds: string[];
      atribuidoPor: string;
      leadsComClienteId?: Array<{ id: string; cliente_id?: string | null }>;
    }) => {
      // Buscar roles dos usuários selecionados
      const { data: usuariosComRole } = await supabase
        .from('usuarios')
        .select('id, role')
        .in('id', consultorIds);

      const rolesMap = new Map(usuariosComRole?.map(u => [u.id, u.role]) || []);

      const dataLimite = new Date();
      dataLimite.setDate(dataLimite.getDate() + 7);

      // === VERIFICAR DONOS COMERCIAIS ===
      // Leads com dono comercial devem ir para o dono, não para o round-robin
      let leadsParaRoundRobin = [...leadIds];
      const leadsRedirecionados = new Map<string, string[]>(); // donoId -> leadIds[]

      if (leadsComClienteId && leadsComClienteId.length > 0) {
        const clienteIds = [...new Set(leadsComClienteId.map(l => l.cliente_id).filter(Boolean))] as string[];
        
        if (clienteIds.length > 0) {
          const donos: Array<{ cliente_id: string; owner_consultor_id: string }> = [];
          for (const parte of emLotes(clienteIds)) {
            const { data } = await supabase
              .from('donos_cliente')
              .select('cliente_id, owner_consultor_id')
              .in('cliente_id', parte)
              .not('owner_consultor_id', 'is', null);
            if (data) donos.push(...(data as any));
          }

          if (donos && donos.length > 0) {
            const donoMap = new Map(donos.map(d => [d.cliente_id, d.owner_consultor_id]));
            
            // Separar leads que têm dono dos que não têm
            const leadsComDono: string[] = [];
            const leadsSemDono: string[] = [];
            
            for (const leadId of leadIds) {
              const leadInfo = leadsComClienteId.find(l => l.id === leadId);
              const donoId = leadInfo?.cliente_id ? donoMap.get(leadInfo.cliente_id) : null;
              
              if (donoId) {
                leadsComDono.push(leadId);
                if (!leadsRedirecionados.has(donoId)) {
                  leadsRedirecionados.set(donoId, []);
                }
                leadsRedirecionados.get(donoId)!.push(leadId);
              } else {
                leadsSemDono.push(leadId);
              }
            }
            
            leadsParaRoundRobin = leadsSemDono;
          }
        }
      }

      // Distribuir leads SEM dono entre usuários (round-robin)
      const distribuicao = new Map<string, string[]>();
      
      leadsParaRoundRobin.forEach((leadId, index) => {
        const usuarioId = consultorIds[index % consultorIds.length];
        if (!distribuicao.has(usuarioId)) {
          distribuicao.set(usuarioId, []);
        }
        distribuicao.get(usuarioId)!.push(leadId);
      });

      // Adicionar leads redirecionados para donos
      for (const [donoId, donoLeadIds] of leadsRedirecionados) {
        if (!distribuicao.has(donoId)) {
          distribuicao.set(donoId, []);
        }
        distribuicao.get(donoId)!.push(...donoLeadIds);
        // Garantir que o donoId está na lista de roles
        if (!rolesMap.has(donoId)) {
          const { data: donoUser } = await supabase
            .from('usuarios')
            .select('role')
            .eq('id', donoId)
            .maybeSingle();
          if (donoUser) rolesMap.set(donoId, donoUser.role);
        }
      }

      let qtdVendedores = 0;
      let qtdGestores = 0;
      // ⚠️ O que ANDOU, somado lote a lote do `count` que a rota devolve. Nao e'
      // o mesmo que `leadIds.length`: o `extra_where` deixa passar so'
      // DISPONIVEL e NEGOCIANDO, e ha' 17.558 leads com consultor fora desses
      // dois status (medido 06/09/2026). Contar o pedido em vez do feito era
      // dizer "50 distribuidos" com 12 parados no lugar.
      let andaram = 0;

      // ✅ BATCH: pré-buscar equipe de todos os consultores de uma vez (elimina N+1)
      const todosUsuarioIds = Array.from(distribuicao.keys());
      const { data: membrosEquipe } = await (supabase
        .from('equipe_membro') as any)
        .select('usuario_id, equipe_id')
        .in('usuario_id', todosUsuarioIds);
      
      const equipeMap = new Map<string, string>();
      membrosEquipe?.forEach((m: any) => {
        equipeMap.set(m.usuario_id, m.equipe_id);
      });

      // ✅ BATCH: acumular todos os históricos para insert único
      const todosHistoricos: Array<any> = [];

      // Atualizar cada grupo de leads por usuário
      for (const [usuarioId, leadsDoUsuario] of distribuicao) {
        const roleDestino = rolesMap.get(usuarioId) || '';
        const isVendedor = ROLES_VENDEDORES.includes(roleDestino);

        // LEI 1c: ATRIBUIR não é trabalhar. O lead vai pro consultor em
        // DISPONIVEL (recebido). Só vira NEGOCIANDO quando ele trabalhar de
        // verdade (email/WhatsApp/ligação — passo 1b). Antes vendedor recebia
        // NEGOCIANDO direto, o que sujava o sinal de "trabalhado".
        const statusNovo = 'DISPONIVEL';

        if (isVendedor) qtdVendedores++;
        else qtdGestores++;

        const updateData: any = {
          consultor_id: usuarioId,
          atribuido_por: atribuidoPor,
          data_atribuicao: new Date().toISOString(),
          status_lead: statusNovo,
          equipe_id: equipeMap.get(usuarioId) || null,
        };

        // Data limite apenas para vendedores
        if (isVendedor) {
          updateData.data_limite_contato = dataLimite.toISOString();
        }

        // Escrita pela ROTA. Sigo em lotes de 200: o corpo agora vai em JSON e
        // nao estoura URL, mas cada lote continua sendo UM update no banco —
        // e' o que mantem a trava curta em cima da tabela.
        for (const parte of emLotes(leadsDoUsuario)) {
          const resposta = await postarNaRota<{ count?: number }>('/v1/leads-carteira/bulk/update', {
            ids: parte,
            updates: updateData,
            extra_where: { status_lead_in: ['DISPONIVEL', 'NEGOCIANDO'] }, // permite redistribuir
          });
          andaram += Number(resposta?.count ?? 0);
        }

        // Acumular histórico para insert batch
        const historico = leadsDoUsuario.map(leadId => ({
          lead_id: leadId,
          acao: 'ATRIBUIDO',
          status_anterior: 'DISPONIVEL',
          status_novo: statusNovo,
          usuario_id: atribuidoPor,
          observacao: isVendedor 
            ? (consultorIds.length > 1 ? `Distribuição para consultores (${consultorIds.length} usuários)` : 'Atribuição para consultor')
            : `Redistribuição para gestão (${roleDestino})`,
          dados: { consultor_destino_id: usuarioId },
        }));

        todosHistoricos.push(...historico);
      }

      // ✅ BATCH: inserir todo o histórico de uma vez
      if (todosHistoricos.length > 0) {
        const { error: histError } = await supabase.from('leads_carteira_historico').insert(todosHistoricos);
        if (histError) console.error('Erro ao gravar histórico de atribuição:', histError);
      }

      // === NOTIFICAÇÕES DE ATRIBUIÇÃO ===
      try {
        const todosIds = [atribuidoPor, ...consultorIds];
        const { data: usuariosNotif } = await supabase
          .from('usuarios')
          .select('id, nome')
          .in('id', todosIds);

        const remetenteNome = usuariosNotif?.find(p => p.id === atribuidoPor)?.nome || 'Alguém';

        // Buscar campanhas dos leads atribuídos (em lotes — evita URL grande → 400)
        const leadsComCampanha: any[] = [];
        for (const parte of emLotes(leadIds)) {
          const { data } = await (supabase.from('leads_carteira') as any)
            .select('campanha_id, campanhas_prospeccao:campanha_id(nome)')
            .in('id', parte)
            .not('campanha_id', 'is', null);
          if (data) leadsComCampanha.push(...data);
        }

        const campanhasUnicas = new Map<string, string>();
        leadsComCampanha?.forEach((l: any) => {
          if (l.campanha_id && l.campanhas_prospeccao?.nome) {
            campanhasUnicas.set(l.campanha_id, l.campanhas_prospeccao.nome);
          }
        });
        const campanhasTexto = campanhasUnicas.size > 0 
          ? Array.from(campanhasUnicas.values()).join(', ')
          : 'Sem campanha';

        // ✅ BATCH: notificar todos os destinatários de uma vez
        const notificacoesBatch: Array<any> = [];
        
        for (const [usuarioId, leadsDoUsuario] of distribuicao) {
          if (usuarioId === atribuidoPor) continue; // Não notificar a si mesmo
          const destinatarioNome = usuariosNotif?.find(p => p.id === usuarioId)?.nome || 'Consultor';
          const qtd = leadsDoUsuario.length;

          notificacoesBatch.push({
            usuario_id: usuarioId,
            tipo: 'leads_recebidos',
            titulo: `📋 Você recebeu ${qtd} lead${qtd > 1 ? 's' : ''}!`,
            mensagem: `${remetenteNome} atribuiu ${qtd} lead${qtd > 1 ? 's' : ''} para você. Campanha: ${campanhasTexto}.`,
            prioridade: 'alta',
            metadata: { 
              lead_ids: leadsDoUsuario, 
              campanha_ids: Array.from(campanhasUnicas.keys()),
              remetente_nome: remetenteNome,
              remetente_id: atribuidoPor,
            },
          });
        }

        // Notificar remetente (confirmação)
        const destinatariosNomes = consultorIds
          .filter(id => id !== atribuidoPor)
          .map(id => usuariosNotif?.find(p => p.id === id)?.nome || 'Consultor');

        // Adicionar notificação do remetente ao batch
        notificacoesBatch.push({
          usuario_id: atribuidoPor,
          tipo: 'leads_transferidos',
          titulo: `✅ Atribuição confirmada`,
          mensagem: `${leadIds.length} lead${leadIds.length > 1 ? 's' : ''} atribuído${leadIds.length > 1 ? 's' : ''} para ${destinatariosNomes.join(', ')}. Campanha: ${campanhasTexto}.`,
          prioridade: 'normal',
          metadata: {
            lead_ids: leadIds,
            destinatarios: consultorIds,
            campanha_ids: Array.from(campanhasUnicas.keys()),
          },
        });

        // ✅ BATCH: inserir todas as notificações de uma vez
        if (notificacoesBatch.length > 0) {
          await supabase.from('notificacoes').insert(notificacoesBatch);
        }
      } catch (notifError) {
        console.error('Erro ao criar notificações de atribuição:', notifError);
      }

      return { 
        count: andaram,
        pedidos: leadIds.length,
        ficaram: Math.max(leadIds.length - andaram, 0),
        consultores: consultorIds.length,
        qtdVendedores,
        qtdGestores,
        distribuicao: Object.fromEntries(
          Array.from(distribuicao.entries()).map(([id, leads]) => [id, leads.length])
        )
      };
    },
    onSuccess: (data) => {
      // Nada andou: nao dizer "atribuido" quando ninguem recebeu nada.
      if (data.count === 0) {
        toast.warning(`Nenhum lead mudou de consultor — os ${data.pedidos} selecionados não estavam em DISPONÍVEL ou NEGOCIANDO.`);
        queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
        queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
        return;
      }
      if (data.ficaram > 0) {
        toast.warning(`${data.count} de ${data.pedidos} atribuídos · ${data.ficaram} ficaram (não estavam em DISPONÍVEL ou NEGOCIANDO)`);
        queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
        queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
        return;
      }
      if (data.qtdGestores > 0 && data.qtdVendedores > 0) {
        toast.success(`✅ ${data.count} leads distribuídos: ${data.qtdVendedores} vendedores + ${data.qtdGestores} gestores`);
      } else if (data.qtdGestores > 0) {
        toast.success(`✅ ${data.count} leads redistribuídos para ${data.qtdGestores} gestor(es) - ficam em Gestão Leads`);
      } else if (data.consultores > 1) {
        toast.success(`✅ ${data.count} leads distribuídos para ${data.consultores} consultores!`);
      } else {
        toast.success(`✅ ${data.count} leads atribuídos com sucesso!`);
      }
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['leads-disponiveis'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao atribuir leads: ${error.message}`);
    },
  });
}

// Transferir leads em massa
export function useTransferirLeadsEmMassa() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadIds,
      consultorOrigemId,
      consultorDestinoId,
      consultorDestinoIds,
      transferidoPor,
      clienteIdsParaTransferir,
    }: {
      leadIds: string[];
      consultorOrigemId?: string;
      consultorDestinoId?: string;
      /** Modo DISTRIBUIÇÃO (multi-destino, sem origem). Quando presente, ignora origem/destino único. */
      consultorDestinoIds?: string[];
      transferidoPor: string;
      clienteIdsParaTransferir?: string[];
    }) => {
      // ════════════════════════════════════════════════════════════════════
      // MODO DISTRIBUIÇÃO (prospecção): vários destinos, SEM origem.
      // Amarrado às leis (ver gatilhos no banco):
      //  • Só move ABERTOS (DISPONIVEL/NEGOCIANDO/PROPOSTA) — fechados nunca (lei de lead).
      //  • Lead SEM dono: sorteio direto entre os destinos (bulk/update).
      //  • Lead de cliente COM dono ativo: o CNPJ passa pela régua da Lei dos
      //    3 Donos na rota /transferencias/comercial/massa — dono lead/negócio
      //    da equipe do supervisor trocam na hora (o gatilho de cascata leva os
      //    leads abertos junto), dono carteira vira pedido na fila do master.
      //    Este front NUNCA escreve em donos_cliente: quem grava é a rota.
      //  • Preserva o status; equipe_id é setada pelo gatilho sync_equipe_on_consultor_change.
      // ════════════════════════════════════════════════════════════════════
      if (consultorDestinoIds && consultorDestinoIds.length > 0) {
        // TENTANDO_CONTATO e' aberto (Lei dos 3 Donos: nivel LEAD) e a cascata do
        // banco tambem o move — deixa-lo fora contava como "fechado" na tela.
        const STATUS_ABERTOS = ['DISPONIVEL', 'TENTANDO_CONTATO', 'NEGOCIANDO', 'PROPOSTA'];
        const chunk = <T,>(arr: T[], size: number): T[][] => {
          const out: T[][] = [];
          for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
          return out;
        };

        // 1) Status, cliente e consultor atual dos selecionados (em lotes p/ não estourar o limite de URI)
        type LeadInfo = { id: string; status_lead: string; cliente_id: string | null; consultor_id: string | null };
        const leadsInfo: LeadInfo[] = [];
        for (const ids of chunk(leadIds, 150)) {
          const { data, error } = await supabase
            .from('leads_carteira')
            .select('id, status_lead, cliente_id, consultor_id')
            .in('id', ids);
          if (error) throw error;
          leadsInfo.push(...((data || []) as LeadInfo[]));
        }

        // 2) Dono ATIVO de cada cliente. Lead de CNPJ com dono não anda sozinho:
        //    o CNPJ vai inteiro, pela régua da Lei dos 3 Donos (passo 6).
        const clienteIds = [...new Set(leadsInfo.map(l => l.cliente_id).filter(Boolean))] as string[];
        const donoAtivoPorCliente = new Map<string, string>();
        for (const ids of chunk(clienteIds, 150)) {
          const { data, error } = await supabase
            .from('donos_cliente')
            .select('cliente_id, owner_consultor_id, usuarios!donos_cliente_owner_consultor_id_fkey(ativo)')
            .in('cliente_id', ids)
            .not('owner_consultor_id', 'is', null);
          if (error) throw error;
          type DonoRow = { cliente_id: string | null; owner_consultor_id: string | null; usuarios: { ativo: boolean | null } | null };
          ((data || []) as unknown as DonoRow[]).forEach((d) => {
            if (d.cliente_id && d.owner_consultor_id && d.usuarios?.ativo === true) {
              donoAtivoPorCliente.set(d.cliente_id, d.owner_consultor_id);
            }
          });
        }

        // 3) Particionar: sem dono (sorteio direto) × com dono (régua) × fechados (nunca)
        const statusById = new Map(leadsInfo.map(l => [l.id, l.status_lead]));
        const moviveis: string[] = [];
        const leadsComDono: LeadInfo[] = [];
        let puladosFechado = 0;
        for (const l of leadsInfo) {
          if (!STATUS_ABERTOS.includes(l.status_lead)) { puladosFechado++; continue; }
          if (l.cliente_id && donoAtivoPorCliente.has(l.cliente_id)) { leadsComDono.push(l); continue; }
          moviveis.push(l.id);
        }

        // 4) Round-robin dos SEM dono entre os destinos
        const distribuicao = new Map<string, string[]>();
        consultorDestinoIds.forEach(id => distribuicao.set(id, []));
        moviveis.forEach((leadId, i) => {
          const destino = consultorDestinoIds[i % consultorDestinoIds.length];
          distribuicao.get(destino)!.push(leadId);
        });

        // 5) UPDATE por grupo, pela ROTA (preserva status; equipe vem do gatilho)
        const nowIso = new Date().toISOString();
        const historico: any[] = [];
        // Quem recebeu o quê — alimenta a notificação (sem dono + com dono).
        const recebidos = new Map<string, string[]>();
        // O que ANDOU, somado do `count` de cada lote — nao o que foi pedido.
        let andaramDistribuicao = 0;
        for (const [destino, ids] of distribuicao) {
          if (ids.length === 0) continue;
          for (const lote of chunk(ids, 120)) {
            const resposta = await postarNaRota<{ count?: number }>('/v1/leads-carteira/bulk/update', {
              ids: lote,
              updates: {
                consultor_id: destino,
                atribuido_por: transferidoPor,
                data_atribuicao: nowIso,
              },
              extra_where: { status_lead_in: STATUS_ABERTOS }, // trava extra: nunca fechados
            });
            andaramDistribuicao += Number(resposta?.count ?? 0);
          }
          recebidos.set(destino, [...ids]);
          ids.forEach(leadId => historico.push({
            lead_id: leadId,
            acao: 'TRANSFERIDO',
            status_anterior: statusById.get(leadId) || null,
            status_novo: statusById.get(leadId) || null,
            usuario_id: transferidoPor,
            observacao: consultorDestinoIds.length > 1
              ? `Distribuição em massa (${consultorDestinoIds.length} consultores)`
              : 'Transferência em massa',
            dados: { consultor_destino_id: destino },
          }));
        }

        // 6) COM DONO: o CNPJ passa pela régua, na rota de transferência de
        //    carteira. Supervisor: DONO LEAD e DONO NEGÓCIO da própria equipe
        //    trocam na hora; DONO CARTEIRA vira pedido na fila do master (os
        //    leads ficam com o dono atual até a decisão). A rota sorteia os
        //    CNPJs entre os destinos e o gatilho de cascata leva os leads
        //    abertos junto com o dono.
        //    Antes (até 09/09/2026) este modo PULAVA todo lead com dono: um
        //    supervisor movendo 300 leads da própria equipe recebia "300 com
        //    dono ficam" e nada andava — a régua nunca era consultada.
        type PlacarNivel = { diretas: number; aguardando: number; negadas: number };
        const lei = {
          diretas: 0, aguardando: 0, solicitacoes: 0, negadas: 0, jaEramDoDestino: 0, foraEscopo: 0,
          motivoNegada: null as string | null,
          // Por nivel do CNPJ (DONO_LEAD / DONO_NEGOCIO / DONO_CARTEIRA): e' o que
          // a tela separa, porque o status do LEAD nao diz o nivel do cliente.
          porNivel: {} as Record<string, PlacarNivel>,
        };
        let erroDonos: string | null = null;
        let andaramPeloDono = 0;
        const clientesComDono = [...new Set(leadsComDono.map(l => l.cliente_id as string))];
        if (clientesComDono.length > 0) {
          try {
            // A fila aceita 100 casos por chamada: acima disso a rota grava o
            // que pode e NÃO enfileira nada — por isso o lote é de 100 CNPJs.
            for (const lote of chunk(clientesComDono, 100)) {
              const r = await postarNaRota<{
                puladosForaEscopo?: number;
                resumoLei?: {
                  diretas?: number; aguardandoAprovacao?: number; solicitacoesCriadas?: number;
                  negadas?: number; jaEramDoDestino?: number; motivoNegada?: string | null;
                  porNivel?: Record<string, Partial<PlacarNivel>>;
                };
              }>('/v1/transferencias/comercial/massa', { clienteIds: lote, consultorIds: consultorDestinoIds });
              const rl = r?.resumoLei || {};
              for (const [nivel, n] of Object.entries(rl.porNivel || {})) {
                const acc = lei.porNivel[nivel] || (lei.porNivel[nivel] = { diretas: 0, aguardando: 0, negadas: 0 });
                acc.diretas += Number(n?.diretas ?? 0);
                acc.aguardando += Number(n?.aguardando ?? 0);
                acc.negadas += Number(n?.negadas ?? 0);
              }
              lei.diretas += Number(rl.diretas ?? 0);
              lei.aguardando += Number(rl.aguardandoAprovacao ?? 0);
              lei.solicitacoes += Number(rl.solicitacoesCriadas ?? 0);
              lei.negadas += Number(rl.negadas ?? 0);
              lei.jaEramDoDestino += Number(rl.jaEramDoDestino ?? 0);
              lei.foraEscopo += Number(r?.puladosForaEscopo ?? 0);
              if (!lei.motivoNegada && rl.motivoNegada) lei.motivoNegada = rl.motivoNegada;
            }
          } catch (e) {
            erroDonos = e instanceof Error && e.message ? e.message : 'a rota de carteira recusou';
          }

          // O que ANDOU de verdade: relê o consultor dos leads com dono. Contar
          // o que a rota "liberou" seria anunciar como transferido o que o
          // gatilho ainda não moveu.
          const destinos = new Set(consultorDestinoIds);
          const antes = new Map(leadsComDono.map(l => [l.id, l.consultor_id]));
          for (const ids of chunk(leadsComDono.map(l => l.id), 150)) {
            const { data } = await supabase.from('leads_carteira').select('id, consultor_id').in('id', ids);
            ((data || []) as Array<{ id: string; consultor_id: string | null }>).forEach((l) => {
              if (!l.consultor_id || !destinos.has(l.consultor_id) || antes.get(l.id) === l.consultor_id) return;
              andaramPeloDono++;
              if (!recebidos.has(l.consultor_id)) recebidos.set(l.consultor_id, []);
              recebidos.get(l.consultor_id)!.push(l.id);
              historico.push({
                lead_id: l.id,
                acao: 'TRANSFERIDO',
                status_anterior: statusById.get(l.id) || null,
                status_novo: statusById.get(l.id) || null,
                usuario_id: transferidoPor,
                observacao: 'Transferência em massa — dono foi junto (Lei dos 3 Donos)',
                dados: { consultor_destino_id: l.consultor_id, consultor_origem_id: antes.get(l.id) ?? null },
              });
            });
          }
        }

        if (historico.length > 0) {
          for (const lote of chunk(historico, 200)) {
            const { error: histError } = await supabase.from('leads_carteira_historico').insert(lote);
            if (histError) console.error('Erro ao gravar histórico de distribuição:', histError);
          }
        }

        // 7) Notificações (uma por destino que recebeu leads)
        try {
          const { data: usuariosNotif } = await supabase
            .from('usuarios').select('id, nome').in('id', [transferidoPor, ...consultorDestinoIds]);
          const remetenteNome = usuariosNotif?.find(u => u.id === transferidoPor)?.nome || 'Alguém';
          const notificacoes: any[] = [];
          for (const [destino, ids] of recebidos) {
            if (ids.length === 0 || destino === transferidoPor) continue;
            const qtd = ids.length;
            notificacoes.push({
              usuario_id: destino,
              tipo: 'leads_recebidos',
              titulo: `Você recebeu ${qtd} lead${qtd > 1 ? 's' : ''}!`,
              mensagem: `${remetenteNome} transferiu ${qtd} lead${qtd > 1 ? 's' : ''} para você. Acesse seus Leads para visualizar.`,
              prioridade: 'alta',
              metadata: { lead_ids: ids, remetente_nome: remetenteNome },
            });
          }
          if (notificacoes.length > 0) await supabase.from('notificacoes').insert(notificacoes);
        } catch (notifError) {
          console.error('Erro ao criar notificações de distribuição:', notifError);
        }

        return {
          count: andaramDistribuicao + andaramPeloDono,
          distribuido: true,
          movidos: andaramDistribuicao + andaramPeloDono,
          nao_andaram: Math.max(moviveis.length - andaramDistribuicao, 0),
          pulados_fechado: puladosFechado,
          com_dono: clientesComDono.length,
          donos_diretas: lei.diretas,
          donos_aguardando: lei.aguardando,
          solicitacoes_criadas: lei.solicitacoes,
          donos_negadas: lei.negadas,
          motivo_negada: lei.motivoNegada,
          por_nivel: lei.porNivel,
          ja_eram_do_destino: lei.jaEramDoDestino,
          fora_escopo: lei.foraEscopo,
          erro_donos: erroDonos,
          consultores: consultorDestinoIds.length,
        };
      }

      // ════════════════════════════════════════════════════════════════════
      // MODO TRANSFERÊNCIA (carteira): origem → destino único.
      //
      // ⛔ Tudo por UMA chamada de ROTA. Daqui saia um UPDATE em `donos_cliente`
      // feito pelo client — trocar o dono de um CNPJ exigia apenas estar logado.
      // A rota e' quem checa cargo, quem decide se a troca vai direto ou para a
      // fila de aprovacao, e quem grava historico e notificacao.
      // ════════════════════════════════════════════════════════════════════
      if (!consultorOrigemId || !consultorDestinoId) {
        throw new Error('Transferência: consultor de origem e destino são obrigatórios.');
      }

      const resposta = await postarNaRota<{
        count?: number;
        pedidos?: number;
        donos_transferidos?: number;
        bloqueados?: Array<{ cliente_id: string; nivel: string | null; motivo: string }>;
        excedente_do_teto?: number;
      }>('/v1/leads-carteira/bulk/transferir', {
        leadIds,
        consultorOrigemId,
        consultorDestinoId,
        transferidoPor,
        clienteIdsParaTransferir,
      });
      const veredito = lerVeredito(resposta);

      // ⚠️ `count` e' quantos ANDARAM; `pedidos`, quantos foram pedidos. Sao
      // numeros diferentes de proposito: o lead so' anda quando o CNPJ dele
      // pode andar. Antes a tela lia o total pedido e anunciava tudo como
      // transferido — 18 leads foram dados como transferidos 22 vezes, um deles
      // em 3 dias seguidos, enquanto o gatilho os devolvia em silencio.
      const andaram = Number(resposta?.count ?? 0);
      const pedidos = Number(resposta?.pedidos ?? leadIds.length);
      // ⛔ Bloqueado NAO foi para fila nenhuma. A rota devolve o cliente em
      // `bloqueados` e escreve um warn no log — ela nunca chama
      // `abrirSolicitacaoDeTroca`. A tela conta quantos sao e por qual nivel,
      // e manda a pessoa para a porta que REALMENTE abre pedido (Busca Lead
      // 360 -> Solicitar transferencia). Dizer "foi para aprovacao" aqui era
      // combinar um gestor esperando decisao com uma fila vazia.
      const bloqueados = resposta?.bloqueados ?? [];
      const porNivel = new Map<string, number>();
      for (const b of bloqueados) porNivel.set(b.nivel || 'sem nível', (porNivel.get(b.nivel || 'sem nível') || 0) + 1);
      return {
        count: andaram,
        pedidos,
        ficaram: Math.max(pedidos - andaram, 0),
        donos: Number(resposta?.donos_transferidos ?? 0),
        excedente: Number(resposta?.excedente_do_teto ?? 0),
        barrados: veredito.barrados,
        barrados_por_nivel: Array.from(porNivel.entries())
          .map(([nivel, n]) => `${n} ${nivel.replace('DONO_', '').toLowerCase()}`)
          .join(', '),
        fila: veredito.fila,
        motivo_fila: veredito.motivo,
      };
    },
    onSuccess: (data: any) => {
      if (data?.distribuido) {
        const s = (n: number) => (n === 1 ? '' : 's');
        const detalhes: string[] = [];
        // Placar por NÍVEL do CNPJ. O status do lead ("Negociando") não diz o
        // nível: o cliente pode ser carteira com lead em negociação, e aí vai
        // para o master. Cada linha diz o nível, quantos e o destino.
        const rotulo: Record<string, string> = { DONO_LEAD: 'DONO LEAD', DONO_NEGOCIO: 'DONO NEGÓCIO', DONO_CARTEIRA: 'DONO CARTEIRA' };
        const porNivel: Record<string, { diretas: number; aguardando: number; negadas: number }> = data.por_nivel || {};
        const ordem = ['DONO_LEAD', 'DONO_NEGOCIO', 'DONO_CARTEIRA', ...Object.keys(porNivel).filter(k => !rotulo[k])];
        let somouPorNivel = false;
        for (const nivel of ordem) {
          const n = porNivel[nivel];
          if (!n) continue;
          const nome = rotulo[nivel] || nivel;
          if (n.diretas) { detalhes.push(`${nome}: ${n.diretas} CNPJ${s(n.diretas)} trocou de dono na hora, os leads foram junto`); somouPorNivel = true; }
          if (n.aguardando) {
            const decide = nivel === 'DONO_CARTEIRA' ? 'do master (só o master transfere carteira)' : 'da gerência';
            detalhes.push(`${nome}: ${n.aguardando} CNPJ${s(n.aguardando)} foi para aprovação ${decide}, fica com o dono atual até a decisão`);
            somouPorNivel = true;
          }
          if (n.negadas) { detalhes.push(`${nome}: ${n.negadas} não pôde (${data.motivo_negada || 'a régua negou'})`); somouPorNivel = true; }
        }
        if (!somouPorNivel) {
          if (data.donos_diretas) detalhes.push(`${data.donos_diretas} CNPJ${s(data.donos_diretas)} trocou de dono na hora, os leads foram junto`);
          if (data.donos_aguardando) detalhes.push(`${data.donos_aguardando} CNPJ${s(data.donos_aguardando)} foi para aprovação, fica com o dono atual até a decisão`);
          if (data.donos_negadas) detalhes.push(`${data.donos_negadas} não puderam: ${data.motivo_negada || 'a régua negou'}`);
        }
        if (data.donos_aguardando) {
          const criadas = Number(data.solicitacoes_criadas ?? 0);
          if (criadas < data.donos_aguardando) {
            detalhes.push(`${data.donos_aguardando - criadas} ficaram fora da fila (teto de 100 por vez), transfira em blocos menores`);
          }
        }
        if (data.ja_eram_do_destino) detalhes.push(`${data.ja_eram_do_destino} já eram do destino`);
        if (data.fora_escopo) detalhes.push(`${data.fora_escopo} de outra equipe ficaram — a troca é com a gerência`);
        if (data.erro_donos) detalhes.push(`leads com dono não andaram: ${data.erro_donos}`);
        if (data.pulados_fechado) detalhes.push(`${data.pulados_fechado} fechado/negado não move (lei)`);
        if (data.nao_andaram > 0) detalhes.push(`${data.nao_andaram} não mudaram de consultor (status fechou a porta)`);
        if (data.movidos === 0) {
          toast.warning([data.donos_aguardando ? 'Nenhum lead mudou agora' : 'Nada foi transferido', ...detalhes].join(' · '));
        } else {
          toast.success([
            `${data.movidos} lead${s(data.movidos)} transferido${s(data.movidos)}`
              + (data.consultores > 1 ? ` entre ${data.consultores} consultores` : ''),
            ...detalhes,
          ].join(' · '));
        }
      } else {
        // A tela conta o que ANDOU e diz o que ficou. Nada de "foi para
        // aprovação" aqui: esta rota barra e devolve o motivo, mas NAO abre
        // pedido nenhum — quem abre e' a Busca Lead 360.
        const detalhes: string[] = [];
        if (data?.ficaram > 0) {
          detalhes.push(`${data.ficaram} ficaram no dono de agora`
            + (data?.barrados_por_nivel ? ` (${data.barrados_por_nivel})` : '')
            + ' — nada foi pedido: use a Busca Lead 360 → Solicitar transferência');
        }
        if (data?.donos > 0) detalhes.push(`${data.donos} cliente${data.donos > 1 ? 's mudaram' : ' mudou'} de dono junto`);
        // So' quando a rota REALMENTE abriu pedido de aprovacao (solicitacao_id
        // ou aguardando_aprovacao). Hoje esta rota nunca abre — a linha existe
        // para o dia em que abrir, e nao fala sozinha.
        if (data?.fila && data?.motivo_fila) detalhes.push(data.motivo_fila);
        if (data?.excedente > 0) detalhes.push(`${data.excedente} ficaram fora do teto de 200 por vez — transfira em blocos`);

        if (data?.count === 0) {
          toast.warning(['Nenhum lead mudou de consultor', ...detalhes].join(' · '));
        } else if (detalhes.length > 0) {
          toast.warning([`${data.count} de ${data.pedidos} transferidos`, ...detalhes].join(' · '));
        } else {
          toast.success(`✅ ${data.count} leads transferidos com sucesso!`);
        }
      }
      // Invalida TODAS as listas de leads (cada página usa uma key diferente).
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['leads-prospeccao-server'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['leads-prospeccao-hierarquia'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['leads-prospeccao-metricas'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads-optimized'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['leads-disponiveis'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao transferir leads: ${error.message}`);
    },
  });
}


// ⛔ NAO existe descarte em massa (Douglas 03/09/2026: "nao existe descarte em
// massa, apagar"). O status DESCARTADO ja estava desativado no banco pelo gatilho
// fn_bloquear_descarte_lead — "Status DESCARTADO foi desativado. Use NEGADO e
// informe o motivo (o lead fica visivel e reativavel)". O hook e o modal
// continuavam nas telas e falhavam SEMPRE, sem ninguem entender por que.
// Para tirar o lead da frente: NEGADO, com motivo.

/**
 * Hook para atribuir lead para o próprio usuário (MASTER/DIRETOR/GERENTE)
 */
export function useAtribuirLeadParaMim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      // ⛔ Pela ROTA, que e' onde a LEI DOS 3 DONOS mora inteira: DONO LEAD sai
      // na hora (com o cliente junto, senao o CNPJ fica com dois nomes),
      // NEGOCIO e CARTEIRA viram pedido de aprovacao, e master continua barrado
      // de se auto-atribuir — a checagem de master saiu daqui porque a rota faz
      // a mesma, com a mesma frase, e regra duplicada e' regra que diverge.
      // A rota tambem grava o historico ATRIBUIDO.
      return postarNaRota(`/v1/leads-carteira/${leadId}/atribuir-para-mim`, {});
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
      const veredito = lerVeredito(data);
      if (veredito.fila) {
        toast.warning(veredito.motivo || 'Foi para aprovação — o lead ainda não é seu.');
        return;
      }
      toast.success('Lead atribuído para você');
    },
    onError: () => {
      toast.error('Erro ao atribuir lead');
    },
  });
}

// Performance por consultor
export function usePerformancePorConsultor(mesReferencia?: string) {
  return useQuery({
    queryKey: ['performance-consultores', mesReferencia],
    queryFn: async () => {
      const { data } = await supabase
        .from('leads_carteira')
        .select(`
          consultor_id,
          status_lead,
          usuarios!consultor_id(nome)
        `)
        .not('consultor_id', 'is', null);

      const grouped = data?.reduce((acc: any, lead: any) => {
        const id = lead.consultor_id;
        if (!acc[id]) {
          acc[id] = {
            consultor_id: id,
            consultorNome: lead.usuarios?.nome || 'Sem Nome',
            ativos: 0,
            convertidos: 0,
            perdidos: 0,
          };
        }

        if (['DISPONIVEL', 'NEGOCIANDO', 'PROPOSTA', 'CARTEIRIZADO'].includes(lead.status_lead)) {
          acc[id].ativos++;
        } else if (lead.status_lead === 'CONVERTIDO') {
          acc[id].convertidos++;
        } else if (['NEGADO', 'VENCIDO'].includes(lead.status_lead)) {
          acc[id].perdidos++;
        }

        return acc;
      }, {});

      // Calcular taxa de conversão
      return Object.values(grouped || {}).map((item: any) => ({
        ...item,
        taxa:
          item.ativos + item.convertidos > 0
            ? Math.round((item.convertidos / (item.ativos + item.convertidos)) * 100)
            : 0,
      })).sort((a: any, b: any) => b.convertidos - a.convertidos);
    },
  });
}

// Leads vencendo
export function useLeadsVencendo(diasLimite: number = 3) {
  return useQuery({
    queryKey: ['leads-vencendo', diasLimite],
    queryFn: async () => {
      const { data } = await supabase
        .from('leads_carteira')
        .select(`
          *,
          usuarios!consultor_id(nome)
        `)
        .in('status_lead', ['DISPONIVEL', 'NEGOCIANDO', 'PROPOSTA', 'CARTEIRIZADO'])
        .not('data_limite_contato', 'is', null)
        .lte('data_limite_contato', addDays(new Date(), diasLimite).toISOString())
        .order('data_limite_contato', { ascending: true });

      return data?.map((lead: any) => ({
        ...lead,
        consultor_nome: lead.usuarios?.nome,
        dias_restantes: differenceInDays(
          new Date(lead.data_limite_contato!),
          new Date()
        ),
      }));
    },
  });
}

// Dados de conversão
export function useDadosConversao(dias: number = 30) {
  return useQuery({
    queryKey: ['dados-conversao', dias],
    queryFn: async (): Promise<Array<{ data: string; convertidos: number; perdidos: number }>> => {
      const { data } = await supabase
        .from('leads_carteira')
        .select('data_conversao, status_lead, created_at')
        .gte('created_at', subDays(new Date(), dias).toISOString());

      // Agrupar por dia
      const grouped = data?.reduce((acc: Record<string, { data: string; convertidos: number; perdidos: number }>, lead: any) => {
        const dataKey = lead.data_conversao
          ? format(new Date(lead.data_conversao), 'dd/MM')
          : format(new Date(lead.created_at), 'dd/MM');

        if (!acc[dataKey]) {
          acc[dataKey] = { data: dataKey, convertidos: 0, perdidos: 0 };
        }

        if (lead.status_lead === 'CONVERTIDO') {
          acc[dataKey].convertidos++;
        } else if (lead.status_lead === 'PERDIDO') {
          acc[dataKey].perdidos++;
        }

        return acc;
      }, {});

      return Object.values(grouped || {}).sort((a, b) =>
        a.data.localeCompare(b.data)
      );
    },
  });
}

/** Colunas que definem de quem e' o lead — nenhuma delas se escreve pelo client. */
const CAMPOS_DE_POSSE = new Set([
  'consultor_id', 'equipe_id', 'atribuido_por', 'data_atribuicao', 'status_lead',
]);

/**
 * Hook para atualizar um campo editável do lead
 */
export function useAtualizarCampoLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      campo,
      valor,
    }: {
      leadId: string;
      campo: string;
      valor: any;
    }) => {
      // ⛔ LEI DOS 3 DONOS — a porta dos fundos deste arquivo. Este hook escreve
      // QUALQUER coluna pelo nome (`{ [campo]: valor }`) direto pelo client:
      // com `campo = 'consultor_id'` ele trocava a posse do lead sem passar por
      // rota, cargo ou veredito nenhum — justamente o que as sete escritas
      // vizinhas acabaram de deixar de fazer. Hoje ele nao tem chamador nenhum
      // (zero usos em apps/frontend/src, conferido 06/09/2026), entao a trava
      // nao muda tela alguma; ela existe para o dia em que alguem reusar o
      // hook e reabrir o buraco sem perceber. Posse se troca pela rota.
      if (CAMPOS_DE_POSSE.has(campo)) {
        throw new Error(
          `"${campo}" é posse do lead e não se troca por aqui. Use a transferência (que passa pela regra dos 3 donos) ou a Busca Lead 360.`,
        );
      }

      const { error } = await supabase
        .from('leads_carteira')
        .update({ 
          [campo]: valor,
          data_ultima_interacao: new Date().toISOString(),
        })
        .eq('id', leadId);

      if (error) throw error;

      // Não registrar mais CAMPO_ATUALIZADO genérico - gera muito ruído
      return { leadId, campo, valor };
    },
    onSuccess: () => {
      toast.success('Campo atualizado!');
      queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
    },
    onError: (error) => {
      console.error('Erro ao atualizar campo:', error);
      toast.error('Erro ao atualizar campo');
    },
  });
}

// Histórico do lead
export function useHistoricoLead(leadId?: string) {
  return useQuery({
    queryKey: ['historico-lead', leadId],
    queryFn: async () => {
      if (!leadId) return [];

      const { data } = await supabase
        .from('leads_carteira_historico')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      return data || [];
    },
    enabled: !!leadId,
  });
}

// ========== ENVIAR LEADS PARA EQUIPE — REMOVIDO em 28/05/2026 ==========
// Fluxo descontinuado. Hook useEnviarLeadsParaEquipe + interface
// EquipeParaDistribuicao + componente ModalEnviarParaEquipe removidos.


/**
 * Hook para reativar leads negados em massa
 */
export function useReativarLeadsEmMassa() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      leadIds,
      novoStatus,
      consultorId,
      usuarioId,
    }: {
      leadIds: string[];
      novoStatus: string;
      consultorId?: string;
      usuarioId: string;
    }) => {
      // Pela ROTA. Ela monta o MESMO updateData (inclusive limpar consultor e
      // equipe quando volta para DISPONIVEL) e ja grava o historico REATIVADO —
      // por isso o registrarReativacao saiu daqui: era historico dobrado.
      const resposta = await postarNaRota<{ count?: number; novoStatus?: string }>(
        '/v1/leads-carteira/bulk/reativar',
        { leadIds, novoStatus, consultorId, usuarioId },
      );
      const veredito = lerVeredito(resposta);

      return {
        count: veredito.fila ? 0 : (resposta?.count ?? leadIds.length),
        novoStatus,
        fila: veredito.fila,
        motivoFila: veredito.motivo,
      };
    },
    onSuccess: (data) => {
      if (data.fila) {
        toast.warning(data.motivoFila || 'Foi para aprovação — os leads ainda não mudaram de consultor.');
        queryClient.invalidateQueries({ queryKey: ['gestao-leads-optimized'] });
        return;
      }
      toast.success(`${data.count} lead${data.count > 1 ? 's' : ''} reativado${data.count > 1 ? 's' : ''} com sucesso!`);
      queryClient.invalidateQueries({ queryKey: ['gestao-leads-optimized'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['leads-disponiveis-count'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao reativar leads: ${error.message}`);
    },
  });
}
